import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FiltroDeInventarios, InventarioDaLista } from '../api/inventarios.js';
import { listarInventarios } from '../api/inventarios.js';
import { listarOperadores } from '../api/operadores.js';
import { intervaloDaPagina, TAMANHO_PAGINA_MAXIMO, totalDePaginas } from '../api/paginacao.js';
import { seInformado } from '../api/recorte.js';
import { useConsulta } from '../api/useConsulta.js';
import { ChipStatus } from '../componentes/ChipStatus.js';
import { formatarDataHora } from '../componentes/datas.js';
import { filialDoFiltro, rotuloDoLocal } from '../componentes/deposito.js';
import { EstadoVazio } from '../componentes/EstadoVazio.js';
import {
  CampoBusca,
  CampoFilial,
  ContadorRegistros,
  FiltroAtivo,
  FiltroSelecao,
} from '../componentes/Filtros.js';
import type { OpcaoFiltro } from '../componentes/Filtros.js';
import { IconeAvancar, IconeInventarios } from '../componentes/Icones.js';
import { Pagina } from '../componentes/Pagina.js';
import { Paginacao } from '../componentes/Paginacao.js';
import { Resultado } from '../componentes/Resultado.js';
import { Tabela } from '../componentes/Tabela.js';
import type { Coluna } from '../componentes/Tabela.js';
import {
  CHIP_INVENTARIO,
  contagemDeCiclos,
  contagemDeCiclosComDivergencia,
  skusContados,
} from './inventarios/formato.js';
import { inicioDoPeriodo, PERIODOS } from './movimentacoes/formato.js';
import { useValorAdiado } from './movimentacoes/useValorAdiado.js';
import './Inventarios.css';

const TODOS = 'todos';

/**
 * Trinta dias, e não sete: ciclo de inventário é evento raro comparado a uma
 * coleta, e um recorte curto abriria a tela vazia num armazém que contou na
 * semana passada.
 */
const PERIODO_PADRAO = '30';

/** Classe do número de divergências: quanto maior, mais forte o tom. */
function tomDivergencias(quantidade: number): string {
  if (quantidade === 0) {
    return 'inventarios__divergencias inventarios__divergencias--zero';
  }

  return quantidade > 2
    ? 'inventarios__divergencias inventarios__divergencias--critico'
    : 'inventarios__divergencias inventarios__divergencias--atencao';
}

function montarColunas(
  aoAbrir: (ciclo: InventarioDaLista) => void,
): readonly Coluna<InventarioDaLista>[] {
  return [
    {
      chave: 'local',
      rotulo: 'Filial · depósito',
      largura: '22%',
      // O local abre o CICLO, não o depósito: `ALM01` teve N contagens ao longo
      // do tempo, e uma rota pelo depósito nunca saberia dizer de qual delas
      // estava falando.
      celula: (ciclo) => (
        <button
          type="button"
          className="tabela__abrir mono"
          onClick={() => {
            aoAbrir(ciclo);
          }}
        >
          {rotuloDoLocal(ciclo.codFil, ciclo.depositoErp)}
        </button>
      ),
    },
    {
      chave: 'operador',
      rotulo: 'Operador',
      largura: '18%',
      // Da contagem MAIS RECENTE; nulo enquanto ninguém contou o depósito.
      celula: (ciclo) =>
        ciclo.operador === null ? (
          '—'
        ) : (
          <span title={ciclo.operador.matricula}>{ciclo.operador.nome}</span>
        ),
    },
    {
      chave: 'data',
      rotulo: 'Data',
      largura: '15%',
      // Relógio do APARELHO (RF-052) — quando o operador contou. Sem contagem,
      // sobra a abertura do ciclo, que é relógio do servidor.
      celula: (ciclo) => (
        <span className="inventarios__discreto">
          {formatarDataHora(ciclo.ultimaContagemEm ?? ciclo.abertoEm)}
        </span>
      ),
    },
    {
      chave: 'itens',
      rotulo: 'Itens contados',
      largura: '13%',
      celula: (ciclo) => skusContados(ciclo.totalSkusUltimaContagem),
    },
    {
      chave: 'divergencias',
      rotulo: 'Divergências',
      largura: '12%',
      celula: (ciclo) => (
        <span className={tomDivergencias(ciclo.divergenciasEmAberto)}>
          {ciclo.divergenciasEmAberto}
        </span>
      ),
    },
    {
      chave: 'status',
      rotulo: 'Status',
      celula: (ciclo) => (
        <ChipStatus
          tom={CHIP_INVENTARIO[ciclo.situacao].tom}
          rotulo={CHIP_INVENTARIO[ciclo.situacao].rotulo}
          tamanho="tabela"
        />
      ),
    },
    {
      chave: 'seta',
      rotulo: '',
      largura: '56px',
      alinhamento: 'direita',
      celula: () => (
        <span className="inventarios__seta">
          <IconeAvancar tamanho={15} />
        </span>
      ),
    },
  ];
}

/**
 * Ciclos de inventário por local (UC12, UC13).
 *
 * Uma linha por CICLO, com paginação e filtros de SERVIDOR. Enquanto esta tela
 * vinha de `dados-exemplo`, ela anunciava 86 ciclos e sete divergências em
 * aberto que nunca existiram — e mandava recontar local que ninguém contou.
 *
 * Abrir ciclo não é ação do painel: quem abre é o ERP (`geracaoinventario`), e
 * por isso não há botão de "nova tarefa" aqui.
 */
export function Inventarios() {
  const navegar = useNavigate();
  const [busca, definirBusca] = useState('');
  const [filial, definirFilial] = useState('');
  const [operadorId, definirOperadorId] = useState(TODOS);
  const [periodo, definirPeriodo] = useState(PERIODO_PADRAO);
  const [somenteDivergencia, definirSomenteDivergencia] = useState(false);
  const [pagina, definirPagina] = useState(1);

  const buscaAdiada = useValorAdiado(busca);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeInventarios>(
    () => ({
      pagina,
      ...seInformado('de', inicioDoPeriodo(periodo)),
      ...seInformado('deposito', buscaAdiada),
      ...seInformado('codFil', codFil),
      ...seInformado('operadorId', operadorId === TODOS ? null : operadorId),
      ...seInformado('comDivergenciaEmAberto', somenteDivergencia ? true : null),
    }),
    [pagina, periodo, buscaAdiada, codFil, operadorId, somenteDivergencia],
  );

  const colunas = useMemo(
    () =>
      montarColunas((ciclo) => {
        void navegar(`/inventarios/${ciclo.id}`);
      }),
    [navegar],
  );

  const buscar = useCallback(() => listarInventarios(filtro), [filtro]);
  const consulta = useConsulta(buscar);
  const comDivergencia = useCiclosComDivergencia();
  const opcoesDeOperador = useOpcoesDeOperador();

  /** Trocar o recorte volta para a primeira página: a 7 do filtro antigo não existe no novo. */
  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <Pagina
      contexto="Inventários"
      titulo="Contagens por local"
      acoes={
        comDivergencia > 0 && (
          <ChipStatus tom="divergencia" rotulo={contagemDeCiclosComDivergencia(comDivergencia)} />
        )
      }
      filtros={
        <>
          <CampoFilial
            valor={filial}
            aoMudar={(valor) => {
              recortar(() => {
                definirFilial(valor);
              });
            }}
          />
          {/* Igualdade exata no servidor: é código do ERP, não trecho de texto. */}
          <CampoBusca
            rotulo="Código do depósito"
            valor={busca}
            aoMudar={(valor) => {
              recortar(() => {
                definirBusca(valor);
              });
            }}
            largura={200}
          />
          <FiltroSelecao
            rotulo="Período"
            valor={periodo}
            opcoes={PERIODOS}
            aoMudar={(valor) => {
              recortar(() => {
                definirPeriodo(valor);
              });
            }}
          />
          <FiltroSelecao
            rotulo="Operador"
            valor={operadorId}
            opcoes={opcoesDeOperador}
            aoMudar={(valor) => {
              recortar(() => {
                definirOperadorId(valor);
              });
            }}
          />
          {/* Filtro de SERVIDOR: recorta o inventário inteiro, não a página carregada. */}
          <FiltroAtivo
            rotulo="Somente com divergência"
            ativo={somenteDivergencia}
            aoAlternar={() => {
              recortar(() => {
                definirSomenteDivergencia((ligado) => !ligado);
              });
            }}
          />
          {consulta.dados !== null && (
            <ContadorRegistros>{contagemDeCiclos(consulta.dados.total)}</ContadorRegistros>
          )}
        </>
      }
    >
      <Resultado
        consulta={consulta}
        espera="Carregando inventários…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            tom="neutro"
            icone={<IconeInventarios tamanho={28} />}
            titulo="Nenhum inventário neste recorte."
            detalhe="Amplie o período ou limpe os filtros para ver outros ciclos de contagem."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={colunas}
            chaveDe={(ciclo) => ciclo.id}
            descricao="Ciclos de inventário por local"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'ciclo', 'ciclos')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </Pagina>
  );
}

/**
 * Quantos ciclos ainda têm divergência esperando decisão (RF-073).
 *
 * É uma segunda consulta, com `tamanho` mínimo: o selo precisa do TOTAL do
 * filtro, e não das linhas — contar o que veio na página diria "4" num armazém
 * com quarenta ciclos pendentes. Enquanto a resposta não chega, o selo não
 * existe; zero também não vira selo, porque nada pendente não é aviso.
 */
function useCiclosComDivergencia(): number {
  const buscar = useCallback(
    () => listarInventarios({ comDivergenciaEmAberto: true, tamanho: 1 }),
    [],
  );

  return useConsulta(buscar).dados?.total ?? 0;
}

/**
 * Operadores do tenant para o filtro por autor.
 *
 * Uma página só, e grande: o filtro é um seletor, não uma listagem. Falha ao
 * carregar não derruba a tela — mas também não passa em branco: o rótulo diz
 * que a lista não veio, senão um seletor com uma opção só pareceria um armazém
 * sem operador nenhum. É o mesmo gancho da tela de Coletas; quando uma terceira
 * listagem precisar dele, ele sobe para `componentes/`.
 */
function useOpcoesDeOperador(): readonly OpcaoFiltro[] {
  const buscar = useCallback(() => listarOperadores({ tamanho: TAMANHO_PAGINA_MAXIMO }), []);
  const { dados, erro } = useConsulta(buscar);

  return useMemo(
    () => [
      {
        valor: TODOS,
        rotulo: erro === null ? 'Operador · todos' : 'Operador · lista indisponível',
      },
      ...(dados?.itens ?? []).map((operador) => ({
        valor: operador.id,
        rotulo: `${operador.nome} · ${operador.matricula}`,
      })),
    ],
    [dados, erro],
  );
}
